/**
 * # Fixed Panel
 *
 *
 * @example
 * new FixedPanel(el);
 *
 * @module components/fixed-panel.js
 */
import BaseComponent from './base';
import addClass from '../helpers/dom/add-class';
import removeClass from '../helpers/dom/remove-class';
import outerHeight from '../helpers/dom/outer-height';
import hasClass from '../helpers/dom/has-class';
import debounce from '../helpers/util/debounce';
import {get as getBreakpoint} from '../helpers/dom/breakpoint';
import {boolean as parseBooleanAttribute, string as parseStringAttribute} from '../helpers/dom/parse-attribute';


class FixedPanel extends BaseComponent {
  /**
   * Fixed Panel constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {

    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }
    this._bindEventListenerCallbacks();
    this._addListeners();
    this._setPanelLocation();
  }


  /**
   * Parse parameters from the elements.
   */
  _parseParams() {
    this.hideOnScroll = this.hideOnScroll || parseBooleanAttribute(this.el, 'data-hide-on-scroll', false);
    this.fixToBottom = this.fixToBottom || parseBooleanAttribute(this.el, 'data-fixed-to-bottom', false);
    this.condenseOnScroll = this.condenseOnScroll || parseBooleanAttribute(this.el, 'data-condense', false);
    this.unlockBreakPoint = this.unlockBreakPoint || parseStringAttribute(this.el, 'data-breakpoint', null);
    this.documentBody = document.querySelector('body');

    // Checks to see if a non-Spark header is provided by the user
    if (this.headerEl !== null) {
      this.foreignHeader = true;
    } else {

      // If no header is given then, search for a Spark header.
      this.headerEl = document.querySelector('.spark-header:not(.spark-header__placeholder)');
      if (this.headerEl) {
        this.headerHideOnScroll = parseBooleanAttribute(this.headerEl, 'data-hide-on-scroll', false);
      }
    }
  }


  /**
   * Add Fixed Panel window listeners
   */
  _addListeners() {
    window.addEventListener('scroll', this._onScrollBound);
    window.addEventListener('resize', this._onResizeBound);

    // if there is a header it may have transitions that change the height, watch for it and adjust accordingly
    if (this.headerEl) {
      if (window.getComputedStyle(this.headerEl).getPropertyValue('transition') !== "") {
        this.headerEl.addEventListener('transitionend', this._onResizeBound);
      }
    }
  }


  /**
   * Remove Fixed Panel window listeners
   */
  _removeListeners() {
    window.removeEventListener('scroll', this._onScrollBound);
    window.removeEventListener('resize', this._onResizeBound);
    if (this.headerEl) {
      if (window.getComputedStyle(this.headerEl).getPropertyValue('transition') !== "") {
        this.headerEl.removeEventListener('transitionend', this._onResizeBound);
      }
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onScrollBound = this._onScroll.bind(this);
    this._onResizeBound = debounce(this._onResize.bind(this), 100);
  }


  /**
   * Sets up references
   * @param  {Element} el base DOM element
   */
  _cacheElements(el) {
    this.el = el;
  }


  /**
   * This method will determine where on the viewport the Fixed Panel will dock to,
   * and gets the dimensions of the Fixed Panel and Header (if fixed to the top of the viewport).
   */
  _setPanelLocation() {
    this.currentScrollPosition = window.pageYOffset !== undefined ? window.pageYOffset : window.document.body.scrollTop;
    this._getPanelDimensions();

    if (this.fixToBottom) {
      // Adding padding to the bottom of the body equivalent to the height of the
      // Fixed Panel when using the "fix to bottom" config
      if (this.documentBody) {
        this.documentBody.style['padding-bottom'] = this.panelHeight + 'px';
      }
    } else if (this.headerEl) {
      // Gets the next Element of page content below the Fixed Panel
      this.pageContent = (this.el.nextSibling).nextSibling;
    }
    if (this.headerPositionFixed) {
      // If a Fixed Header exists, append the Fixed Panel to it and sets the
      // top margin of the parent of the Header to the height of the Header and Fixed Panel
      this.headerEl.appendChild(this.el);
      this.headerEl.parentNode.style['margin-top'] = outerHeight(this.headerEl) + this.panelHeight + 24 + 'px';

      // If the header hides on scroll, but the panel scrolls to an element,
      // make it fix to the top of the viewport until that element
      if (this.headerHideOnScroll && this.scrolledToEl) {
        addClass(this.el, 'fix-to-fixed-header');
        addClass(this.headerEl, 'hide-top-show-panel');
      } else if (this.headerHideOnScroll && this.hideOnScroll) {
        addClass(this.headerEl, 'hide-top-with-panel');
      }
    }
  }


  /**
   * This method checks the current viewport size against the breakpoint
   * set in the configs, returning false if the viewport size is less
   * than or equal to the breakpoint config and true if it is not.
   */
  _checkBreakPoint() {

    // Gets the current breakpoint of the current window
    let windowWidth = window.innerWidth;
    let currentBreakpoint = getBreakpoint(windowWidth);

    // Returns false if the current breakpoint is equal to the config breakpoint
    // or any other breakpoint that is smaller than the breakpoint config
    if (this.unlockBreakPoint === currentBreakpoint) {
      return false;
    }

    if (this.unlockBreakPoint === 'sm' && currentBreakpoint === 'xs') {
      return false;
    }

    if ((this.unlockBreakPoint === 'md') && (currentBreakpoint === 'sm' || currentBreakpoint === 'xs')) {
      return false;
    }

    if ((this.unlockBreakPoint === 'lg') && (currentBreakpoint === 'md' || currentBreakpoint === 'sm' || currentBreakpoint === 'xs')) {
      return false;
    }

    if (this.unlockBreakPoint === 'xl') {
      return false;
    }

    return true;
  }

  /**
   * This method fixes the Fixed Panel to the top of the viewport when a user has
   * scrolled beyond the Header or if no Header is present by default fixes to the
   * top of viewport.
   */
  _fixToTopViewport() {
    const lockPanel = this._checkBreakPoint();

    if (this.currentScrollPosition > this.headerHeight) {
      if (lockPanel) {
        // When scrolled past the height of the Header, the Fixed Panel will fix
        // itself to the top of the viewport
        if (!hasClass(this.el, 'is-fixed-top')) {
          addClass(this.el, 'is-fixed-top');

          // Add top margin to the next element that is under the Fixed Panel
          this.pageContent.style['margin-top'] = this.panelHeight + 'px';
        }

        // Method call to condense the Fixed Panel, if the condense config is set
        this.condensePanel();
      }
    } else {
      if (this.headerHeight !== 0) {
        // Upon returning to the bottom of a present header the Fixed Panel will
        // dock to the bottom of the header
        if (hasClass(this.el, 'is-fixed-top')) {
          removeClass(this.el, 'is-fixed-top');

          // Eliminates top margin to the next element that is under the Fixed Panel
          this.pageContent.style['margin-top'] = 0 + 'px';
        }
      }

      // Method call to extend the Fixed Panel from a condensed state if the condense config is set
      this.extendPanel();
    }
  }


  /**
   * This method will show or hide the Fixed panel if it is currently out of view
   * and the user scrolls up or down
   */
  _showPanelOnScroll() {
    const totalHeight = this.headerHeight + this.panelHeight;

    // If scrolled down the height of the Header, and the Fixed Panel the Panel will hide.
    // If scrolled up the Fixed Panel will show, and if the header is in the viewport the Fix Panel
    // will dock to the bottom of the Header
    if (this.currentScrollPosition > totalHeight) {
      if (this.currentScrollPosition < this.previousScrollPosition) {
        this.showPanel();
      } else if (this.currentScrollPosition >= this.previousScrollPosition) {
        this.hidePanel();
      }
    }
  }


  /**
   * Trigger Event function is called to show the panel based off of a desired event
   */
  showPanel() {
    if (this.fixToBottom) {
      removeClass(this.el, 'hide-panel-bottom');
      this.documentBody.style['padding-bottom'] = this.panelHeight + 'px';
    } else {
      removeClass(this.el, 'hide-panel-top');
    }
  }


  /**
   * Trigger Event function is called to hide the panel based off of a desired event
   */
  hidePanel() {
    if (this.fixToBottom) {
      addClass(this.el, 'hide-panel-bottom');
      this.documentBody.style['padding-bottom'] = 0;
    } else {
      addClass(this.el, 'hide-panel-top');
    }
  }


  /**
   * Trigger Event function is called to condense the panel based off of a desired event
   */
  condensePanel() {

    // If a condense config is added than this checks for the a custom condense class
    // to apply to the Fixed Panel, if no class is provided than apply default class 'spark-panel--condensed'
    if (this.condenseOnScroll) {
      if (this.customCondenseClass) {
        addClass(this.el, this.customCondenseClass);
      } else {
        addClass(this.el, 'spark-panel--condensed');
      }
    }
  }


  /**
   * Trigger Event function is called to extend the panel from a condensed state based off of a desired event
   */
  extendPanel() {
    if (this.condenseOnScroll) {
      if (this.customCondenseClass) {
        removeClass(this.el, this.customCondenseClass);
      } else {
        removeClass(this.el, 'spark-panel--condensed');
      }
    }
  }


  /**
   * This method gets the height and location of the Header and also updates the
   * height of the Fixed Panel and Fixed Panel PlaceHolder to allow for the appropriate window modifications
   */
  _getPanelDimensions() {
    this.panelHeight = this.el.getBoundingClientRect().height;

    if (this.fixToBottom) {
      addClass(this.el, 'is-fixed-bottom');
    } else if (this.headerEl) {
      this.headerTop = this.headerEl.getBoundingClientRect().top + this.currentScrollPosition;
      this.headerBottom = this.headerEl.getBoundingClientRect().bottom + this.currentScrollPosition;
      this.headerHeight = this.headerTop + outerHeight(this.headerEl);

      // Checks to see if the Header is fixed or not
      const position = hasClass(this.headerEl, 'spark-header--fixed') ? 'fixed' : window.getComputedStyle(this.headerEl, null).getPropertyValue('position');
      const lockPanel = this._checkBreakPoint();

      // If the Header is fixed no Header calculations are need, if not the height of the Header
      // based off it's position on the page is calculated
      if (position === 'fixed') {
        this.headerPositionFixed = true;
      } else {
         this.headerHeight = outerHeight(this.headerEl) + this.headerTop;
      }
      if (!lockPanel) {
        removeClass(this.el, 'is-fixed-top');
        if (this.pageContent) {
          this.pageContent.style['margin-top'] = 0;
        }
      }
    } else {
      const lockPanel = this._checkBreakPoint();
      if (lockPanel) {
        // Disregarding other configs, by default if a header is not provided and a
        // Spark header is not present Fixed Panel will fix to the top of viewport
        addClass(this.el, 'is-fixed-top');
        this.el.style['margin-bottom'] = this.panelHeight + 'px';
        this.headerHeight = 0;
      }
    }
  }


  /**
   * Update the margin or padding of the body element or Header based on the position of
   * the Fixed Panel and presence of a Header.
   */
  _updatePanels() {
    if (this.fixToBottom) {
      this.documentBody.style['padding-bottom'] = this.panelHeight + 'px';
    }

    // Checks the current breakpoint aganist that of the breakpoint config to determine
    // if the Fixed Panel will remian fixed to the top viewport
    const lockPanel = this._checkBreakPoint();

    if (!this.headerEl && !this.fixToBottom && lockPanel) {
      addClass(this.el, 'is-fixed-top');
      this.documentBody.style['padding-top'] = this.panelHeight + 'px';
    }

    // Updates the margin of the parent node of the header to account for page content on resize
    if (this.headerPositionFixed) {
      this.headerEl.parentNode.style['margin-top'] = outerHeight(this.headerEl) + this.panelHeight + 24 + 'px';
    }
  }


  /**
   * On any resize event this method will recalculate the height of the header
   * as the Header may condense based off of window size, and adjusts the margin/padding
   * of elements if the Fixed Panel content wraps because of a smaller viewport size
   */
  _onResize() {
    this._getPanelDimensions();
    this.panelHeight = this.el.getBoundingClientRect().height;
    if (this.pageContent && parseInt(this.pageContent.style['margin-top']) > 0) {
      this.pageContent.style['margin-top'] = this.panelHeight + 'px';
    }

    this._updatePanels();
  }


  /**
   * On any scroll event this method will apply which ever variation and/or
   * config to the Fixed Panel on a scroll event
   */
  _onScroll() {
    this.previousScrollPosition = this.currentScrollPosition;
    this.currentScrollPosition = window.pageYOffset !== undefined ? window.pageYOffset : window.document.body.scrollTop;
    this._getPanelDimensions();

    if (this.fixToBottom) {
      if (this.condenseOnScroll) {
        this._condensePanelonScroll();
      }
    } else {
      if (this.headerPositionFixed) {
        if (this.condenseOnScroll) {
          if (!this.foreignHeader) {
            this._onHeaderCondense();
          } else {
            this._condensePanelonScroll();
          }
        }
      } else if (!this.headerPositionFixed) {
        if (this.hideOnScroll) {
          this._showPanelOnScroll();
          this._fixToTopViewport();
        } else {
          this._fixToTopViewport();
        }
      }
    }

    this._showUntilElement();
  }


  /**
   * Add a condensed class to the Fixed Panel when scrolled an amount of pixels greater
   * than the height of the Fixed Panel
   */
  _condensePanelonScroll() {
    if (this.currentScrollPosition > this.panelHeight) {
      this.condensePanel();
    } else {
      this.extendPanel();
    }
  }


  /**
   * This method will show/hide the Fixed panel on scroll based on its position
   * relative to a specified page-level element (scrolledToEl)
   */
  _showUntilElement() {
    if (!this.scrolledToEl || this.fixToBottom) {
      return;
    }

    let targetScrollPosition;

    // Calculates the scroll position of the targeted element for a Fixed Panel
    if (this.headerEl && hasClass('spark-header--fixed')) {
      // If the Header is fixed, the height of the header is factored into the calculations
      targetScrollPosition = (this.scrolledToEl.getBoundingClientRect().top + window.pageYOffset) - (outerHeight(this.el) + outerHeight(this.headerEl));
    } else {
      targetScrollPosition = (this.scrolledToEl.getBoundingClientRect().top + window.pageYOffset) - (outerHeight(this.el));
    }

    // If the current scroll position is greater or equal to the top of the target position
    // than the Fixed Panel hides, else it remains visible
    if (this.currentScrollPosition >= targetScrollPosition && this.currentScrollPosition > 0) {
      if (this.transitionComplete) {
        this.hidePanel();
      }
      if (this.transitionComplete && this.headerHideOnScroll && this.scrolledToEl && this.headerEl.contains(this.el)) {
        this.transitionComplete = false;
        let self = this;

        // Prevents the panel from 'snapping' around due to the transition into and out of
        // the header in the markup by only doing so at transitionend
        window.addEventListener('transitionend', function makeElementSibling(e) {
          if (!e || self.el.getBoundingClientRect().bottom >= 0) return;
          self.headerEl.parentNode.insertBefore(self.el, self.headerEl.nextElementSibling);
          self.transitionComplete = true;
          this.window.removeEventListener('transitionend', makeElementSibling, false);
          if (!(self.currentScrollPosition > 0)) {
            self.showPanel();
          }
        }, false);
      }
    } else {
      if (this.transitionComplete) {
        this.showPanel();
      }
      if (this.transitionComplete && this.headerHideOnScroll && this.scrolledToEl && !this.headerEl.contains(this.el)) {
        this.transitionComplete = false;
        let self = this;

        // Prevents the panel from 'snapping' around due to the transition into and out of
        // the header in the markup by only doing so at transitionend
        window.addEventListener('transitionend', function makeElementChild(e) {
          if (!e || self.el.getBoundingClientRect().top < 0) return;
          self.headerEl.appendChild(self.el);

          // Edge was redoing the transition after moving the HTML, so we remove the transition
          // markup when it's done moving so it doesnt reanimate. Abandon all hope ye who enter here
          if (this.navigator.userAgent.indexOf('Edge') > -1) {
            this.addEventListener('transitionstart', function stopTransition(e) {
              if (!e || self.el.getBoundingClientRect().top >= targetScrollPosition) return;
              self.el.style.transform = 'none';
              self.el.style.transition = '0s';

              // Need to wait for it to stop or else it won't actually remove the style
              this.addEventListener('transitionend', function removeStyles(e) {
                if (!e) return;
                self.el.removeAttribute('style');
                this.removeEventListener('transitionend', removeStyles, false);
              }, false);
              this.removeEventListener('transitionstart', stopTransition, false);
            }, false);
          }
          self.transitionComplete = true;
          this.window.removeEventListener('transitionend', makeElementChild, false);
        }, false);
      }
    }
  }


  /**
   * This method will apply/disable the condensed class onto the Fixed Panel at the same time as the Header requires
   * use of the Spark Header.
   */
  _onHeaderCondense() {
    if (hasClass(this.headerEl, "spark-header--condensed")) {
      this.condensePanel();
    } else {
      this.extendPanel();
    }
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
*/
FixedPanel.prototype._whitelistedParams = [
  'headerEl',
  'scrolledToEl',
  'unlockBreakPoint',
  'hideOnScroll',
  'condenseOnScroll',
  'fixToBottom',
  'customCondenseClass'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
FixedPanel.prototype.defaults = {
  el: null,
  scrolledToEl: null,
  headerEl: null,
  headerPosition: null,
  headerHeight: null,
  headerTop: null,
  headerPositionFixed: null,
  previousScrollPosition: null,
  currentScrollPosition: null,
  hideOnScroll: null,
  condenseOnScroll: null,
  fixToBottom: null,
  unlockBreakPoint: null,
  customCondenseClass: null,
  foreignHeader: null,
  panelHeight: null,
  transitionComplete: true,
  documentBody: null,
  _onScrollBound: null,
  _onResizeBound: null
};

export default FixedPanel;
